import { useState, useEffect, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { ulid } from 'ulid';
import { saveAppState } from '@/util/app-state';
import { formatCurrency } from '@/util/currency';
import Button from '@/components/Button';
import CircleCheckFilledIcon from '@/components/icons/CircleCheckFilledIcon';
import Loading from '@/components/Loading';
import { keySubscriptionProducts } from '@/api/query-keys';
import { getProductCatalog, createCheckoutSession, openPortalSession } from '@/api/service/stripe.service';
import { SubscriptionLevel, SubscriptionStatus, type Subscription } from '@/api/model/profile';
import Authorized from '@/components/Authorized';
import type { SubscriptionProductRecord } from '@/models/stripe';
import { classes } from '@/util/component-utils';
import { useAuthorization, subscriptionSelector } from '@/hooks/useAuthorization.hook';
import ISO8601 from '@/util/iso-dates';
import styles from './subscription.module.css';

interface PlanPriceProps {
    priceFor: SubscriptionLevel;
}

function PlanPrice(props: PlanPriceProps) {
    const [priceMessage, setPriceMessage] = useState("Loading...");
    const [productCatalog, setProductCatalog] = useState<SubscriptionProductRecord | undefined>();
    const { data, isLoading } = useQuery({
        queryKey: keySubscriptionProducts(), 
        queryFn: getProductCatalog
    });

    useEffect(() => {
        if (!isLoading && data){
            setProductCatalog(data);
        }
    }, [isLoading, data]);

    useEffect(() => {   
        if (productCatalog) {
            const product = productCatalog[props.priceFor];
            if (product && product.price && product.price.amount) {
                const price = formatCurrency(product.price.amount, product.price.currency ?? "usd", false);
                setPriceMessage(`${price} -  per-year`)
            } else {
                setPriceMessage("error");
            }
        }
    }, [productCatalog]);

    return (
        <div className={styles.price}>
            {priceMessage}
        </div>
    );
                    
}

interface AccountButtonProps {
    subscription?: Subscription;
}

function AccountButton(props: AccountButtonProps) {
    const currentRoutePath = useLocation();
    const handleAccountBilling = async () => {
        if (!props.subscription?.customerId) {
            console.warn("subscription or customer id is undefined");
            return;
        }
        openPortalSession(currentRoutePath.pathname, props.subscription.customerId);
    }

    return (
        <Button appearance='primary' className={styles.buyButton} style={{ width: "170px" }} onClick={handleAccountBilling}>Account & Billing</Button>
    )
}

interface PlanButtonProps {
    buttonFor: SubscriptionLevel;
    currentLevel?: SubscriptionLevel;
    currentStatus?: SubscriptionStatus;
}

function PlanButton(props: PlanButtonProps) {
    const [productCatalog, setProductCatalog] = useState<SubscriptionProductRecord | undefined>();
    const { data, isLoading } = useQuery({
        queryKey: keySubscriptionProducts(), 
        queryFn: getProductCatalog
    });
    const [label, setLabel] = useState("Buy");
    const [isCurrentPlan, setIsCurrentPlan] = useState(false);

    useEffect(() => {
        if (!isLoading && data){
            setProductCatalog(data);
        }
    }, [isLoading, data]);

    const planButtonLabel = () => {
        if (!props.currentLevel || !props.currentStatus || props.currentStatus === SubscriptionStatus.NONE || props.currentLevel === SubscriptionLevel.NONE) {
            return "Buy";
        }

        const planOrder = [
            SubscriptionLevel.NONE,
            SubscriptionLevel.STUDENT,
            SubscriptionLevel.PRO,
            SubscriptionLevel.ORGANIZER
        ];
        const currentPlanIndex = planOrder.indexOf(props.currentLevel);
        const planTypeIndex = planOrder.indexOf(props.buttonFor);

        if (currentPlanIndex === planTypeIndex) return "Active";
        if (currentPlanIndex < planTypeIndex) return "Upgrade";
        if (currentPlanIndex > planTypeIndex) return "Downgrade";
        return "Buy";
    }

    useEffect(() => {
        setLabel(planButtonLabel());
        setIsCurrentPlan(props.currentLevel === props.buttonFor);
    }, [props.currentLevel, props.currentStatus]);

    const buyClick = async () => {
        const sessionKey = ulid();
        const returnUrl = `${window.location.origin}/checkedout?key=${sessionKey}&session_id={CHECKOUT_SESSION_ID}`;
        if (!productCatalog) {
            console.error("Catalog not loaded");
            return;
        }
        const priceId = productCatalog[props.buttonFor]?.price.id;
        if (!priceId) {
            console.error(`Catalog does not contain price for subscription level "${props.buttonFor}"`);
            return;
        }
        const checkoutSession = await createCheckoutSession(priceId, returnUrl);
        if (!checkoutSession || !checkoutSession.checkoutUrl) {
            console.warn("Failed to create Strip checkout session.");
            return;
        }
        saveAppState(sessionKey);
        // throw redirect({ href: checkoutSession.checkoutUrl }); // documented way to redirect, but generates exception. Why?
        window.location.href = checkoutSession.checkoutUrl;
    }

    if (isLoading) {
        return (
            <div className={styles.buttonLoading}>
                <Loading size="sm" />
            </div>
        )
    }

    return (
        <Button disabled={isCurrentPlan} appearance='primary' className={styles.buyButton} onClick={isCurrentPlan ? undefined : () => buyClick()}>{label}</Button>
    )
}

interface TrialMessageProps {
    messageFor: SubscriptionLevel;
    subscription?: Subscription;
}

function TrialMessage(props: TrialMessageProps) {
    const [trialMessage, setTrialMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!props.subscription?.trialExpiration || props.messageFor != props.subscription?.type) return;
        const nowISO = ISO8601.nowUTC();
        const trialEndISO = props.subscription.trialExpiration;
        const now = parseISO(nowISO);
        const trialEnd = parseISO(trialEndISO);
        if (now > trialEnd) {
            // TODO: check payment status with stripe and upgrade subscription unless payment was cancelled.
            console.log(`Trial ended ${format(trialEnd, "E MMM dd yyyy")}`)
        } else {
            const formattedEndDate = format(trialEnd, "E MMM dd yyyy");
            setTrialMessage(`Trial ends ${formattedEndDate}`);
        }
    }, [props.subscription]);

    return (<span className={styles.trialMessage}>{trialMessage}</span>)
}

interface BulletRowProps {
    text: string;
}

function BulletRow(props: BulletRowProps) {
    return (
        <li className={styles.bulletRow}>
            <CircleCheckFilledIcon size={{ width: 20, height: 20 }} position={{ x: 0, y: -1 }} />
            <span className={styles.bulletText}>{props.text}</span>
        </li>
    )
}

interface ProductNameProps {
    nameFor: SubscriptionLevel;
}

function ProductName(props: ProductNameProps) {
    const [css, setCss] = useState(styles.student);
    const [name, setName] = useState("Subscription");
    const [productCatalog, setProductCatalog] = useState<SubscriptionProductRecord | undefined>();
    const { data, isLoading } = useQuery({
        queryKey: keySubscriptionProducts(), 
        queryFn: getProductCatalog
    });

    useEffect(() => {
        if (!isLoading && data){
            setProductCatalog(data);
        }
    }, [isLoading, data]);

    useEffect(() => {
        if (!isLoading && productCatalog && productCatalog[props.nameFor]) {
            setName(productCatalog[props.nameFor]!.name);
            switch (props.nameFor) {
                case SubscriptionLevel.STUDENT:
                    setCss(styles.student);
                    break;
                case SubscriptionLevel.PRO:
                    setCss(styles.pro);
                    break;
                case SubscriptionLevel.ORGANIZER:
                    setCss(styles.organizer);
                    break;
            }
        }
    }, [productCatalog]);

    return (
        <div className={styles.plan}>
            <span className={classes(styles.planName, css)}>
                {name}
            </span>
        </div>
    )
}

interface PlanFeaturesProps {
    featuresFor: SubscriptionLevel;
}

function PlanFeatures(props: PlanFeaturesProps) {
    const [features, setFeatures] = useState<string[]>([""]);
    const [productCatalog, setProductCatalog] = useState<SubscriptionProductRecord | undefined>();
    const { data, isLoading } = useQuery({
        queryKey: keySubscriptionProducts(), 
        queryFn: getProductCatalog
    });

    useEffect(() => {
        if (!isLoading && data){
            setProductCatalog(data);
        }
    }, [isLoading, data]);

    useEffect(() => {
        if (!isLoading && productCatalog && productCatalog[props.featuresFor]) {
            setFeatures(productCatalog[props.featuresFor]!.features);
        }
    }, [productCatalog]);

    return (
        <ul className={styles.bulletsContainer}>
            { features.map((feature, index) => (
                <BulletRow key={index} text={feature} />
            )) }
        </ul>
    );
}

function SubscriptionOptions() {
    const plans = [SubscriptionLevel.STUDENT, SubscriptionLevel.PRO, SubscriptionLevel.ORGANIZER];
    const subscription = useAuthorization(subscriptionSelector);

    // console.log(`PROFILE: ${JSON.stringify(Authorizor.profile)}`); // DEBUG
    // console.log(`SUBSCRIPTION: ${JSON.stringify(subscription)}`); // DEBUG
    
    return (
        <Fragment>
            <div className={styles.container}>
                { plans.map((plan, index) => (
                    <section key={index} className={styles.subscriptionPanel}>
                        <div className={styles.subscriptionInner}>
                            <div className={styles.planRow}>
                                <ProductName nameFor={plan} />
                            </div>
                            <PlanPrice priceFor={plan} />
                            <PlanFeatures featuresFor={plan} />
                        </div>
                        <div className={styles.bottomContainer}>
                            <PlanButton buttonFor={plan} currentLevel={subscription?.type ?? SubscriptionLevel.NONE} currentStatus={subscription?.status ?? SubscriptionStatus.NONE} />
                            <TrialMessage messageFor={plan} subscription={subscription} />
                        </div>
                    </section>
                ))}
            </div>
            {/* Conditionally render the Account & Billing button */}
            {subscription?.type && subscription?.type != SubscriptionLevel.NONE && (
                <div className={styles.accountContainer}>
                    <AccountButton subscription={subscription} />
                </div>
            )}
        </Fragment>
    );
}


export default function Subscription() {
    return (
        <Authorized>
            <SubscriptionOptions />
        </Authorized>
    )
}